/**
 * Modul "Kassensystem": eigenständiges Kassenbuch für die Bargeldverwaltung
 * des Clubs - Nachfolger des entfernten Moduls "Kasse" (siehe Migration
 * 0037), führt aber bewusst eine komplett eigene, unabhängige Buchführung
 * (kassensystem_accounts/kassensystem_transactions, siehe Migration 0032).
 * Auswahlseite mit zwei Bereichen (Grüngeld/Schwarzgeld) - beide sind
 * inzwischen vollständig funktionsfähig (siehe Migration 0038 für Grüngeld).
 *
 * "Aktueller Betrag" IST der Kontostand (kassensystem_accounts.balance) - es
 * gibt bewusst keine separate Soll-/Ist-/Differenz-Anzeige: sowohl manuelle
 * Buchungen als auch automatisch über Discord synchronisierte Bewegungen
 * wirken direkt auf denselben Betrag.
 *
 * Schwarzgeld und Grüngeld werden von ZWEI KOMPLETT UNTERSCHIEDLICHEN
 * Discord-Quellen synchronisiert (unterschiedliche Bots, Channels und
 * Nachrichtenformate - siehe backend/src/scheduled.ts bzw.
 * backend/src/kassensystemGruenSync.ts), daher CashAccountRegister als
 * gemeinsame Buchungs-/Historie-/Korrektur-Ansicht, aber mit jeweils eigener
 * Sync-Status-Karte (SyncStatusCard/GruenSyncStatusCard).
 *
 * Zugriff: Berechtigung "kassensystem.view" (siehe Migration 0036), ab Werk
 * President/VizePresident/Secretary/Treasurer/Phantom - der
 * Systemadministrator hat unabhängig davon weiterhin Zugriff (siehe
 * App.tsx/Sidebar.tsx/MobileDrawer.tsx).
 */
import { useCallback, useEffect, useState, type FormEvent, type ReactNode } from "react";
import { api, ApiError } from "../../api/client";
import { Card, CardHeader } from "../../components/ui/Card";
import { Badge } from "../../components/ui/Badge";
import { StatCard } from "../../components/ui/StatCard";
import { Modal } from "../../components/ui/Modal";
import { Spinner, EmptyState } from "../../components/ui/Spinner";
import { formatDateTime } from "../../utils/formatDate";
import type {
  KassensystemAccount, KassensystemGruenSyncStatus, KassensystemSyncStatus, KassensystemTransaction,
  KassensystemVaultCorrection, KassensystemVaultStatus, PaginatedResult,
} from "../../types";

const currency = (n: number) => n.toLocaleString("de-DE", { style: "currency", currency: "EUR" });

type View = "select" | "green" | "black";

export default function Kassensystem() {
  const [view, setView] = useState<View>("select");

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl font-semibold text-gray-100">Kassensystem</h1>
        <p className="text-sm text-gray-500 mt-1">Bargeldverwaltung des Clubs.</p>
      </div>

      {view === "select" && (
        <>
          <SelectionScreen onSelect={setView} />
          {/* Schwarz zu Grün Rechner: sichtbar für alle Rollen mit Zugriff
              auf dieses Modul (die Route ist bereits über "kassensystem.view"
              geschützt, das "Member" nicht besitzt - siehe Migration 0049 -
              daher entspricht dies "alle Rollen außer Member"). */}
          <WashCalculator />
        </>
      )}
      {view === "green" && (
        <CashAccountRegister
          accountKey="green"
          shortLabel="Grüngeld"
          tone="success"
          heading="Grüngeld-Kassensystem"
          subheading="Eigenständiges Kassenbuch für Grüngeld (Firmenkonto)."
          amountLabel="Aktueller Grüngeldbetrag"
          correctionLabel="Kontostand"
          renderSyncCard={(onSynced) => <GruenSyncStatusCard onSynced={onSynced} />}
          onBack={() => setView("select")}
        />
      )}
      {view === "black" && (
        <CashAccountRegister
          accountKey="black"
          shortLabel="Schwarzgeld"
          tone="danger"
          heading="Schwarzgeld-Kassensystem"
          subheading="Eigenständiges Kassenbuch für Schwarzgeld."
          amountLabel="Aktueller Schwarzgeldbetrag"
          correctionLabel="Tresor"
          renderSyncCard={(onSynced) => <SchwarzgeldSyncStatusCard onSynced={onSynced} />}
          onBack={() => setView("select")}
        />
      )}
    </div>
  );
}

function SelectionScreen({ onSelect }: { onSelect: (v: "green" | "black") => void }) {
  const [accounts, setAccounts] = useState<KassensystemAccount[] | null>(null);

  useEffect(() => {
    api.get<KassensystemAccount[]>("/api/kassensystem/accounts").then(setAccounts).catch(() => setAccounts([]));
  }, []);

  const balanceFor = (key: "green" | "black") => accounts?.find((a) => a.key === key)?.balance;

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
      <button
        type="button"
        onClick={() => onSelect("green")}
        className="card p-10 flex flex-col items-center gap-4 border border-transparent hover:border-success/50 transition-colors"
      >
        <span className="text-6xl font-display font-bold text-success">$</span>
        <span className="text-lg font-display font-semibold uppercase tracking-wide text-gray-200">Grüngeld</span>
        <span className="text-sm text-gray-500">{accounts ? currency(balanceFor("green") ?? 0) : "…"}</span>
      </button>
      <button
        type="button"
        onClick={() => onSelect("black")}
        className="card p-10 flex flex-col items-center gap-4 border border-transparent hover:border-danger/50 transition-colors"
      >
        <span className="text-6xl font-display font-bold text-danger">$</span>
        <span className="text-lg font-display font-semibold uppercase tracking-wide text-gray-200">Schwarzgeld</span>
        <span className="text-sm text-gray-500">{accounts ? currency(balanceFor("black") ?? 0) : "…"}</span>
      </button>
    </div>
  );
}

/** "Schwarz zu Grün Rechner": rein clientseitiger Hilfsrechner unter der Grün-/
 *  Schwarzgeld-Auswahl - errechnet aus einem eingegebenen Schwarzgeldbetrag
 *  und einem frei wählbaren Prozentsatz (Waschgebühr) das daraus werdende
 *  Grüngeld. Bewusst OHNE Speicherung/Buchung - reine Vorschau-Rechnung, die
 *  keinerlei Konto/Kassenbuch berührt; wer das Ergebnis tatsächlich bucht,
 *  trägt es anschließend ganz normal über "Neue Buchung" ein. Sichtbar für
 *  alle Rollen mit Zugriff auf das Kassensystem (siehe Aufrufstelle in
 *  Kassensystem() weiter unten). */
function WashCalculator() {
  const [blackAmount, setBlackAmount] = useState<number | "">("");
  const [percentage, setPercentage] = useState<number | "">("");

  const hasInput = blackAmount !== "" && percentage !== "";
  const fee = hasInput ? Math.round(Number(blackAmount) * (Number(percentage) / 100) * 100) / 100 : null;
  const result = hasInput ? Math.round((Number(blackAmount) - (fee ?? 0)) * 100) / 100 : null;

  return (
    <Card>
      <CardHeader title="Schwarz zu Grün Rechner" subtitle="Schwarzgeld in Grüngeld umrechnen - die Waschgebühr wird prozentual abgezogen." />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
        <div>
          <label className="label">Schwarzgeld-Betrag (€)</label>
          <input
            className="input"
            type="number"
            step="0.01"
            min={0}
            value={blackAmount}
            onChange={(e) => setBlackAmount(e.target.value === "" ? "" : Number(e.target.value))}
            placeholder="z.B. 10000"
          />
        </div>
        <div>
          <label className="label">Waschgebühr (%)</label>
          <input
            className="input"
            type="number"
            step="0.1"
            min={0}
            max={100}
            value={percentage}
            onChange={(e) => setPercentage(e.target.value === "" ? "" : Number(e.target.value))}
            placeholder="z.B. 30"
          />
        </div>
      </div>
      {result !== null ? (
        <div className="rounded-lg px-4 py-3 bg-success-muted border border-success/30 flex items-center justify-between flex-wrap gap-3">
          <div>
            <p className="text-xs text-gray-500 uppercase tracking-wide">Ergebnis (Grüngeld)</p>
            <p className="text-2xl font-display font-bold text-success">{currency(result)}</p>
          </div>
          <p className="text-xs text-gray-500 text-right">
            Waschgebühr ({percentage}%)<br />
            <span className="text-danger font-medium">-{currency(fee ?? 0)}</span>
          </p>
        </div>
      ) : (
        <p className="text-sm text-gray-500">Betrag und Gebühr eingeben, um das Ergebnis zu sehen.</p>
      )}
    </Card>
  );
}

/** Kleiner Ampel-Punkt für den Sync-Status - grün = läuft fehlerfrei, rot =
 *  nicht eingerichtet oder mit Fehlern - schneller erfassbar als reiner Text. */
function StatusDot({ ok }: { ok: boolean }) {
  return (
    <span className={`inline-block w-2.5 h-2.5 rounded-full shrink-0 ${ok ? "bg-success" : "bg-danger"}`} title={ok ? "Läuft fehlerfrei" : "Nicht eingerichtet oder mit Fehlern"} />
  );
}

/** Breadcrumb statt reinem "Zurück"-Link - zeigt zusätzlich, in welchem
 *  Konto man sich gerade befindet (hilfreich beim Wechseln zwischen
 *  Grüngeld/Schwarzgeld). */
function Breadcrumb({ current, onBack }: { current: string; onBack: () => void }) {
  return (
    <div className="flex items-center gap-1.5 text-sm">
      <button type="button" className="text-gray-500 hover:text-gray-300 hover:underline" onClick={onBack}>Kassensystem</button>
      <span className="text-gray-600">/</span>
      <span className="text-gray-300 font-medium">{current}</span>
    </div>
  );
}

function CashAccountRegister({ accountKey, shortLabel, tone, heading, subheading, amountLabel, correctionLabel, renderSyncCard, onBack }: {
  accountKey: "green" | "black";
  shortLabel: string;
  tone: "success" | "danger";
  heading: string;
  subheading: string;
  amountLabel: string;
  correctionLabel: string;
  renderSyncCard: (onSynced: () => void) => ReactNode;
  onBack: () => void;
}) {
  const [account, setAccount] = useState<KassensystemAccount | null>(null);
  const [vaultStatus, setVaultStatus] = useState<KassensystemVaultStatus | null>(null);
  const [corrections, setCorrections] = useState<KassensystemVaultCorrection[]>([]);
  const [transactions, setTransactions] = useState<KassensystemTransaction[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [showCorrection, setShowCorrection] = useState(false);
  const pageSize = 25;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [accounts, status, correctionsResult, txResult] = await Promise.all([
        api.get<KassensystemAccount[]>("/api/kassensystem/accounts"),
        api.get<KassensystemVaultStatus>(`/api/kassensystem/vault-status?accountKey=${accountKey}`),
        api.get<KassensystemVaultCorrection[]>(`/api/kassensystem/vault-corrections?accountKey=${accountKey}`),
        api.get<PaginatedResult<KassensystemTransaction>>(`/api/kassensystem/transactions?accountKey=${accountKey}&page=${page}&pageSize=${pageSize}`),
      ]);
      setAccount(accounts.find((a) => a.key === accountKey) ?? null);
      setVaultStatus(status);
      setCorrections(correctionsResult);
      setTransactions(txResult.items);
      setTotal(txResult.total);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, accountKey]);

  useEffect(() => { load(); }, [load]);

  async function handleNoteSaved() {
    load();
  }

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div className="space-y-6">
      <Breadcrumb current={shortLabel} onBack={onBack} />

      <div>
        <h2 className="text-lg font-semibold text-gray-100">{heading}</h2>
        <p className="text-sm text-gray-500 mt-1">{subheading}</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <StatCard
          label={amountLabel}
          value={vaultStatus ? currency(vaultStatus.balance) : "…"}
          icon={<span>💵</span>}
          tone={tone}
          trend={vaultStatus?.lastCorrectedAt ? `Zuletzt korrigiert: ${formatDateTime(vaultStatus.lastCorrectedAt)}` : "Noch nicht korrigiert"}
        />
        {renderSyncCard(load)}
      </div>

      <Card>
        <CardHeader title="Neue Buchung" subtitle="Ein- oder Auszahlung erfassen" />
        <BookingForm accountKey={accountKey} onBooked={load} />
      </Card>

      <Card>
        <CardHeader
          title={`${correctionLabel} korrigieren`}
          subtitle={`Trage den tatsächlichen ${correctionLabel === "Tresor" ? "Bestand" : "Kontostand"} ein, um den Betrag zu korrigieren.`}
          action={<button className="btn-danger" onClick={() => setShowCorrection(true)}>{correctionLabel} korrigieren</button>}
        />
        {corrections.length === 0 ? (
          <p className="text-sm text-gray-500">Noch keine Korrektur vorgenommen.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="table-base">
              <thead><tr><th>Zeitpunkt</th><th>Vorher</th><th>Neu</th><th>Differenz</th><th>Notiz</th><th>Von</th></tr></thead>
              <tbody>
                {corrections.map((cor) => (
                  <tr key={cor.id}>
                    <td className="text-gray-500 text-xs whitespace-nowrap">{formatDateTime(cor.created_at)}</td>
                    <td className="text-gray-300">{currency(cor.previous_balance)}</td>
                    <td className="text-gray-200 font-medium">{currency(cor.new_balance)}</td>
                    <td className={cor.difference >= 0 ? "text-success" : "text-danger"}>{cor.difference >= 0 ? "+" : ""}{currency(cor.difference)}</td>
                    <td className="text-gray-500">{cor.note ?? "—"}</td>
                    <td className="text-gray-400">{cor.corrected_by}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Card padded={false} className="overflow-x-auto">
        <div className="px-5 pt-4">
          <CardHeader title="Buchungshistorie" subtitle="Alle Ein- und Auszahlungen im Kassensystem" />
        </div>
        {loading ? <Spinner /> : transactions.length === 0 ? <EmptyState message="Keine Buchungen vorhanden." /> : (
          <table className="table-base">
            <thead><tr><th>Datum</th><th>Art</th><th>Kategorie</th><th>Notiz</th><th>Betrag</th><th>Saldo danach</th><th>Gebucht von</th></tr></thead>
            <tbody>
              {transactions.map((t) => (
                <tr key={t.id}>
                  <td className="text-gray-400 whitespace-nowrap">{formatDateTime(t.created_at)}</td>
                  <td>{t.type === "deposit" ? <Badge tone="success">Einzahlung</Badge> : <Badge tone="danger">Auszahlung</Badge>}</td>
                  <td className="text-gray-300">{t.category}</td>
                  <td className="text-gray-500"><NoteCell transaction={t} onSaved={handleNoteSaved} /></td>
                  <td className={t.type === "deposit" ? "text-success font-medium" : "text-danger font-medium"}>
                    {t.type === "deposit" ? "+" : "-"}{currency(t.amount)}
                  </td>
                  <td className="text-gray-200">{currency(t.balance_after)}</td>
                  <td className="text-gray-400">{t.booked_by ?? <span className="italic">Discord-Sync</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 text-sm">
          <button className="btn-secondary px-3 py-1" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Zurück</button>
          <span className="text-gray-400">Seite {page} von {totalPages}</span>
          <button className="btn-secondary px-3 py-1" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>Weiter</button>
        </div>
      )}

      {showCorrection && account && vaultStatus && (
        <VaultCorrectionModal
          account={account}
          correctionLabel={correctionLabel}
          currentBalance={vaultStatus.balance}
          onClose={() => setShowCorrection(false)}
          onSaved={() => { setShowCorrection(false); load(); }}
        />
      )}
    </div>
  );
}

function SchwarzgeldSyncStatusCard({ onSynced }: { onSynced: () => void }) {
  const [status, setStatus] = useState<KassensystemSyncStatus | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [syncMessage, setSyncMessage] = useState<{ tone: "success" | "danger"; text: string } | null>(null);
  const [editingKey, setEditingKey] = useState(false);
  const [keyValue, setKeyValue] = useState("");
  const [savingKey, setSavingKey] = useState(false);

  const loadStatus = useCallback(async () => {
    const result = await api.get<KassensystemSyncStatus>("/api/kassensystem/sync-status");
    setStatus(result);
    setKeyValue(result.tresorStorageKey);
  }, []);

  useEffect(() => { loadStatus(); }, [loadStatus]);

  async function handleSync() {
    setSyncing(true);
    setSyncMessage(null);
    try {
      const result = await api.post<{ processed: number }>("/api/kassensystem/sync-now");
      setSyncMessage({
        tone: "success",
        text: result.processed > 0
          ? `Synchronisierung abgeschlossen: ${result.processed} neue Meldung(en) verarbeitet.`
          : "Synchronisierung abgeschlossen: keine neuen Meldungen im Discord-Channel.",
      });
      loadStatus();
      onSynced();
    } catch (err) {
      setSyncMessage({ tone: "danger", text: err instanceof ApiError ? err.message : "Synchronisierung fehlgeschlagen." });
    } finally {
      setSyncing(false);
    }
  }

  async function handleSaveKey() {
    setSavingKey(true);
    try {
      await api.put("/api/kassensystem/tresor-key", { storageKey: keyValue.trim() || undefined });
      setEditingKey(false);
      loadStatus();
    } catch (err) {
      alert(err instanceof ApiError ? err.message : "Tresor-Schlüssel konnte nicht gespeichert werden.");
    } finally {
      setSavingKey(false);
    }
  }

  return (
    <div className="card p-5 flex flex-col gap-3">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide flex items-center gap-1.5">
            <StatusDot ok={!!status?.configured && status.errorCount === 0} />
            Discord-Synchronisierung
          </p>
          <p className="text-sm text-gray-300 mt-2">
            {status?.configured ? "Automatisch, alle 60 Sekunden" : "Kein Tresor-Schlüssel eingerichtet"}
          </p>
          <p className="text-xs text-gray-500 mt-1">
            {status?.lastEventAt ? `Letzte Meldung: ${formatDateTime(status.lastEventAt)}` : "Noch keine Meldung empfangen"}
            {status && status.errorCount > 0 && <span className="text-danger"> · {status.errorCount} Fehler</span>}
          </p>
        </div>
        <button className="btn-primary whitespace-nowrap" onClick={handleSync} disabled={syncing}>
          {syncing ? "Synchronisiere…" : "🔄 Synchronisieren"}
        </button>
      </div>
      {syncMessage && (
        <div className={`text-xs rounded-lg px-3 py-2 ${syncMessage.tone === "success" ? "bg-success-muted text-success" : "bg-danger-muted text-danger"}`}>
          {syncMessage.text}
        </div>
      )}
      <div className="pt-2 border-t border-surface-border/60">
        {editingKey ? (
          <div className="flex items-center gap-2">
            <input className="input py-1 text-xs" value={keyValue} onChange={(e) => setKeyValue(e.target.value)} placeholder="z.B. blackreaper_tresor" autoFocus />
            <button className="text-accent text-xs hover:underline shrink-0" disabled={savingKey} onClick={handleSaveKey}>{savingKey ? "…" : "Speichern"}</button>
            <button className="text-gray-500 text-xs hover:underline shrink-0" disabled={savingKey} onClick={() => { setEditingKey(false); setKeyValue(status?.tresorStorageKey ?? ""); }}>Abbrechen</button>
          </div>
        ) : (
          <button type="button" className="text-xs text-gray-500 hover:text-gray-300 hover:underline" onClick={() => setEditingKey(true)}>
            Tresor-Schlüssel: <span className="text-gray-400">{status?.tresorStorageKey || "nicht gesetzt"}</span> (ändern)
          </button>
        )}
      </div>
    </div>
  );
}

/** Grüngeld wird von einem eigenen Discord-Bot/Channel synchronisiert (siehe
 *  Migration 0038) - eigener Status/eigene Einstellungen (Channel-ID +
 *  Firmenschlüssel statt Tresor-Storage-Schlüssel). */
function GruenSyncStatusCard({ onSynced }: { onSynced: () => void }) {
  const [status, setStatus] = useState<KassensystemGruenSyncStatus | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [syncMessage, setSyncMessage] = useState<{ tone: "success" | "danger"; text: string } | null>(null);
  const [editing, setEditing] = useState(false);
  const [channelValue, setChannelValue] = useState("");
  const [firmaValue, setFirmaValue] = useState("");
  const [saving, setSaving] = useState(false);

  const loadStatus = useCallback(async () => {
    const result = await api.get<KassensystemGruenSyncStatus>("/api/kassensystem/gruen/sync-status");
    setStatus(result);
    setChannelValue(result.channelId);
    setFirmaValue(result.firmaKey);
  }, []);

  useEffect(() => { loadStatus(); }, [loadStatus]);

  async function handleSync() {
    setSyncing(true);
    setSyncMessage(null);
    try {
      const result = await api.post<{ processed: number }>("/api/kassensystem/gruen/sync-now");
      setSyncMessage({
        tone: "success",
        text: result.processed > 0
          ? `Synchronisierung abgeschlossen: ${result.processed} neue Meldung(en) verarbeitet.`
          : "Synchronisierung abgeschlossen: keine neuen Meldungen im Discord-Channel.",
      });
      loadStatus();
      onSynced();
    } catch (err) {
      setSyncMessage({ tone: "danger", text: err instanceof ApiError ? err.message : "Synchronisierung fehlgeschlagen." });
    } finally {
      setSyncing(false);
    }
  }

  async function handleSave() {
    setSaving(true);
    try {
      await Promise.all([
        api.put("/api/kassensystem/gruen/channel-id", { channelId: channelValue.trim() || undefined }),
        api.put("/api/kassensystem/gruen/firma-key", { firmaKey: firmaValue.trim() || undefined }),
      ]);
      setEditing(false);
      loadStatus();
    } catch (err) {
      alert(err instanceof ApiError ? err.message : "Einstellungen konnten nicht gespeichert werden.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="card p-5 flex flex-col gap-3">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide flex items-center gap-1.5">
            <StatusDot ok={!!status?.configured && status.errorCount === 0} />
            Discord-Synchronisierung
          </p>
          <p className="text-sm text-gray-300 mt-2">
            {status?.configured ? "Automatisch, alle 60 Sekunden" : "Kein Channel eingerichtet"}
          </p>
          <p className="text-xs text-gray-500 mt-1">
            {status?.lastEventAt ? `Letzte Meldung: ${formatDateTime(status.lastEventAt)}` : "Noch keine Meldung empfangen"}
            {status && status.errorCount > 0 && <span className="text-danger"> · {status.errorCount} Fehler</span>}
          </p>
        </div>
        <button className="btn-primary whitespace-nowrap" onClick={handleSync} disabled={syncing}>
          {syncing ? "Synchronisiere…" : "🔄 Synchronisieren"}
        </button>
      </div>
      {syncMessage && (
        <div className={`text-xs rounded-lg px-3 py-2 ${syncMessage.tone === "success" ? "bg-success-muted text-success" : "bg-danger-muted text-danger"}`}>
          {syncMessage.text}
        </div>
      )}
      <div className="pt-2 border-t border-surface-border/60">
        {editing ? (
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <label className="text-xs text-gray-500 w-20 shrink-0">Channel-ID</label>
              <input className="input py-1 text-xs" value={channelValue} onChange={(e) => setChannelValue(e.target.value)} placeholder="z.B. 1543933781944311888" autoFocus />
            </div>
            <div className="flex items-center gap-2">
              <label className="text-xs text-gray-500 w-20 shrink-0">Firma</label>
              <input className="input py-1 text-xs" value={firmaValue} onChange={(e) => setFirmaValue(e.target.value)} placeholder="z.B. blackreaper" />
            </div>
            <div className="flex items-center gap-2">
              <button className="text-accent text-xs hover:underline shrink-0" disabled={saving} onClick={handleSave}>{saving ? "…" : "Speichern"}</button>
              <button className="text-gray-500 text-xs hover:underline shrink-0" disabled={saving} onClick={() => { setEditing(false); setChannelValue(status?.channelId ?? ""); setFirmaValue(status?.firmaKey ?? ""); }}>Abbrechen</button>
            </div>
          </div>
        ) : (
          <button type="button" className="text-xs text-gray-500 hover:text-gray-300 hover:underline text-left" onClick={() => setEditing(true)}>
            Channel: <span className="text-gray-400">{status?.channelId || "nicht gesetzt"}</span> · Firma: <span className="text-gray-400">{status?.firmaKey || "nicht gesetzt"}</span> (ändern)
          </button>
        )}
      </div>
    </div>
  );
}

/** Notiz einer Buchung inline anzeigen und per Klick nachträglich setzen/ändern. */
function NoteCell({ transaction, onSaved }: { transaction: KassensystemTransaction; onSaved: () => void }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(transaction.note ?? "");
  const [saving, setSaving] = useState(false);

  async function handleSave() {
    setSaving(true);
    try {
      await api.put(`/api/kassensystem/transactions/${transaction.id}/note`, { note: value.trim() || undefined });
      setEditing(false);
      onSaved();
    } catch (err) {
      alert(err instanceof ApiError ? err.message : "Notiz konnte nicht gespeichert werden.");
    } finally {
      setSaving(false);
    }
  }

  if (editing) {
    return (
      <div className="flex items-center gap-1.5">
        <input
          className="input py-1 text-xs min-w-[10rem]"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder="Grund der Buchung"
          autoFocus
          onKeyDown={(e) => { if (e.key === "Enter") handleSave(); if (e.key === "Escape") setEditing(false); }}
        />
        <button className="text-accent text-xs hover:underline shrink-0" disabled={saving} onClick={handleSave}>{saving ? "…" : "OK"}</button>
      </div>
    );
  }

  return (
    <button type="button" className="text-left hover:underline decoration-dotted" onClick={() => setEditing(true)}>
      {transaction.note ?? <span className="italic text-gray-600">Notiz hinzufügen</span>}
    </button>
  );
}

function BookingForm({ accountKey, onBooked }: { accountKey: "green" | "black"; onBooked: () => void }) {
  const [type, setType] = useState<"deposit" | "withdrawal">("deposit");
  const [amount, setAmount] = useState<number | "">("");
  const [category, setCategory] = useState("");
  const [note, setNote] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (amount === "" || Number(amount) <= 0) { setError("Bitte einen gültigen Betrag eingeben."); return; }
    if (!category.trim()) { setError("Bitte eine Kategorie angeben."); return; }
    setSubmitting(true);
    try {
      await api.post("/api/kassensystem/transactions", {
        accountKey, type, amount: Number(amount), category: category.trim(), note: note.trim() || undefined,
      });
      setAmount("");
      setCategory("");
      setNote("");
      onBooked();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Buchung fehlgeschlagen.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {error && <div className="bg-danger-muted text-danger text-sm rounded-lg px-3 py-2">{error}</div>}

      <div className="grid grid-cols-2 gap-3">
        <button
          type="button"
          onClick={() => setType("deposit")}
          className={`py-2 rounded text-sm font-display font-medium uppercase tracking-wide border transition-colors ${type === "deposit" ? "bg-success-muted text-success border-success/40" : "bg-surface-raised text-gray-400 border-surface-border"}`}
        >
          Einzahlung
        </button>
        <button
          type="button"
          onClick={() => setType("withdrawal")}
          className={`py-2 rounded text-sm font-display font-medium uppercase tracking-wide border transition-colors ${type === "withdrawal" ? "bg-danger-muted text-danger border-danger/40" : "bg-surface-raised text-gray-400 border-surface-border"}`}
        >
          Auszahlung
        </button>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="label">Betrag (€) *</label>
          <input className="input" type="number" step="0.01" min={0.01} value={amount} onChange={(e) => setAmount(e.target.value === "" ? "" : Number(e.target.value))} required />
        </div>
        <div>
          <label className="label">Kategorie *</label>
          <input className="input" value={category} onChange={(e) => setCategory(e.target.value)} placeholder="z.B. Club Einnahmen, Ausgabe" required />
        </div>
      </div>

      <div>
        <label className="label">Notiz (Grund der Buchung)</label>
        <input className="input" value={note} onChange={(e) => setNote(e.target.value)} placeholder="z.B. Verwendungszweck" />
      </div>

      <button className="btn-primary w-full py-2.5" disabled={submitting}>
        {submitting ? "Wird gebucht…" : type === "deposit" ? "Einzahlung buchen" : "Auszahlung buchen"}
      </button>
    </form>
  );
}

function VaultCorrectionModal({ account, correctionLabel, currentBalance, onClose, onSaved }: {
  account: KassensystemAccount; correctionLabel: string; currentBalance: number; onClose: () => void; onSaved: () => void;
}) {
  const [newBalance, setNewBalance] = useState<number | "">(currentBalance);
  const [note, setNote] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const preview = newBalance === "" ? null : Math.round((Number(newBalance) - currentBalance) * 100) / 100;

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (newBalance === "" || Number(newBalance) < 0) { setError("Bitte einen gültigen Betrag eingeben."); return; }
    setSubmitting(true);
    try {
      await api.post("/api/kassensystem/vault-correction", {
        accountKey: account.key, newBalance: Number(newBalance), note: note.trim() || undefined,
      });
      onSaved();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Korrektur fehlgeschlagen.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      title={`${correctionLabel} korrigieren`}
      onClose={() => !submitting && onClose()}
      footer={<>
        <button className="btn-secondary" onClick={onClose} disabled={submitting}>Abbrechen</button>
        <button className="btn-primary" form="vault-correction-form" disabled={submitting}>{submitting ? "Wird übernommen…" : "Übernehmen"}</button>
      </>}
    >
      <form id="vault-correction-form" onSubmit={handleSubmit} className="space-y-4">
        {error && <div className="bg-danger-muted text-danger text-sm rounded-lg px-3 py-2">{error}</div>}
        <div className="flex items-center justify-between text-sm px-1">
          <span className="text-gray-500">Aktueller Betrag</span>
          <span className="text-gray-100 font-semibold">{currency(currentBalance)}</span>
        </div>
        <div>
          <label className="label">Tatsächlicher Betrag (€) *</label>
          <input
            className="input"
            type="number"
            step="0.01"
            min={0}
            value={newBalance}
            onChange={(e) => setNewBalance(e.target.value === "" ? "" : Number(e.target.value))}
            required
            autoFocus
          />
        </div>
        {preview !== null && (
          <div className={`rounded-lg px-3 py-2 text-sm ${preview === 0 ? "bg-success-muted text-success" : "bg-danger-muted text-danger"}`}>
            Differenz zum aktuellen Betrag: {preview >= 0 ? "+" : ""}{currency(preview)}
          </div>
        )}
        <div>
          <label className="label">Notiz / Begründung</label>
          <input className="input" value={note} onChange={(e) => setNote(e.target.value)} placeholder="optional" />
        </div>
      </form>
    </Modal>
  );
}
